import { pacificToday } from '../../../../shared/dates';
import type { IsoDate } from '../../../../shared/types';
import type { Snapshot } from '../../data/store';
import { inPersonOnly } from '../../logic/activity';
import { closeDates, deltaOf, periodToDate, visitDay } from '../../logic/home';
import type { Delta, Period, PeriodCount } from '../../logic/home';
import { ownerTalkSeries, periodSeries, rateDelta } from '../../logic/overview';
import { h, span } from '../../ui/dom';

/** Bars for the last periods, the current one in the accent; an empty period stays a stub. */
export const sparkline = (values: readonly (number | null)[]): HTMLElement => {
  const max = Math.max(1, ...values.map((v) => v ?? 0));
  return h(
    'div',
    { class: 'spark', 'aria-hidden': 'true' },
    values.map((v, i) =>
      h('span', {
        class: ['spark__bar', i === values.length - 1 ? 'spark__bar--now' : ''].filter(Boolean).join(' '),
        style: { height: `${Math.max(6, Math.round((100 * (v ?? 0)) / max))}%` },
      }),
    ),
  );
};

const tile = (label: string, value: string, delta: Delta, series: readonly (number | null)[]): HTMLElement =>
  h(
    'div',
    { class: 'card ov-kpi' },
    span('ov-kpi__label', label),
    span('ov-kpi__value', value),
    h('span', { class: `ov-kpi__delta ov-kpi__delta--${delta.tone}` }, delta.text),
    sparkline(series),
  );

const countTile = (label: string, p: PeriodCount, series: number[]): HTMLElement =>
  tile(label, String(p.current), deltaOf(p), series);

/** The period's four numbers, each against the same stretch before and with its last five periods. */
export const kpiRow = (snap: Snapshot, today: IsoDate, period: Period): HTMLElement => {
  // "Visits" and the owner-talk rate count in-person touches only; a close can happen on a call
  // and counts whatever channel it came on (D90)
  const inPerson = inPersonOnly(snap.visits);
  const visits = inPerson.map(visitDay);
  const leads = snap.leads.map((l) => pacificToday(new Date(l.created_at)));
  const closes = closeDates(snap.visits);
  const owner = ownerTalkSeries(inPerson, today, period);
  const now = owner[owner.length - 1] ?? null;
  const before = owner[owner.length - 2] ?? null;
  return h(
    'div',
    { class: 'ov-kpis' },
    countTile('Visits', periodToDate(visits, today, period), periodSeries(visits, today, period)),
    countTile('New leads', periodToDate(leads, today, period), periodSeries(leads, today, period)),
    countTile('Closes', periodToDate(closes, today, period), periodSeries(closes, today, period)),
    tile(
      'Talked to the owner',
      now === null ? '—' : `${now}%`,
      rateDelta(now, before, period === 'week' ? 'last week' : 'last month'),
      owner,
    ),
  );
};
